import { asMutableQueryParams } from 'Src/types/view-helpers';
import URI from 'urijs';
import { convertLocationToURI, convertUrlToOrigin } from 'Utils/uri/uriUtils';
type Args = {
  asPath: string;
  originalUrl: string;
  query: Record<string, string | string[] | void>;
  params?: Record<string, string | string[] | void> | void;
};
export const createRouterLocation = ({
  asPath,
  originalUrl,
  query,
  params = {},
}: Args): RouterLocation => {
  const uri = new URI(asPath);
  const urlParams = {};
  const queryParams = {};

  /*
   * Due to a next-route implementation with a weird `.join('/')`
   * we need to check if the current query param has a slash as separator
   * and split it into an array.
   * cf: https://github.com/fridays/next-routes/issues/250
   */
  for (const queryParameter in query) {
    if (uri.hasSearch(queryParameter) && !params[queryParameter]) {
      queryParams[queryParameter] = query[queryParameter];
    } else {
      urlParams[queryParameter] = query[queryParameter];
    }
  }

  return {
    origin:
      typeof window !== 'undefined' ? window.location.origin : convertUrlToOrigin(originalUrl),
    pathname: uri.pathname(),
    hash: uri.hash(),
    urlParams,
    queryParams,
  };
};
export const updateShallowQueryParams = ({
  location,
  href,
}: {
  location: RouterLocation;
  // no href server-side since the shallow routing only happens client side
  href: string | null | undefined;
}): RouterLocation => {
  if (!href) {
    return location;
  }

  const uri = convertLocationToURI(location);
  // We cannot reconstruct the URL simply from href since the front-end doesn't know what routes can
  // be rewritten (/r/Bali/y is {urlParams: { dynamicPage: '/r/Bali/y }})
  const currentUri = new URI(href);
  const newQueryParams = asMutableQueryParams(location.queryParams);
  const queryParamsList = Object.keys(uri.query(true));
  const currentQueryParamsList = Object.keys(currentUri.query(true));

  // delete query params which are no longer in the new uri
  for (const queryParamName in uri.query(true)) {
    if (!currentQueryParamsList.includes(queryParamName)) {
      delete newQueryParams[queryParamName];
    }
  }

  // add missing query params or update the current ones
  for (const queryParamName in currentUri.query(true)) {
    if (
      !queryParamsList.includes(queryParamName) ||
      newQueryParams[queryParamName] !== currentUri.query(true)[queryParamName]
    ) {
      newQueryParams[queryParamName] = currentUri.query(true)[queryParamName];
    }
  }

  return { ...location, queryParams: newQueryParams as RouterLocation['queryParams'] };
};
